// FILE: src/components/PerformanceHistory.js
//
// Phase B portal overlay — learner-facing Performance History.
//
// Reads from learner_session_summary via performanceHistoryService. Layout
// (top to bottom):
//   1. Header bar: title + close.
//   2. Cert filter chips — only certs the learner has data in (per the
//      "honest about what's trackable" guardrail from the design doc).
//   3. Summary strip: count, best %, avg %, last attempt date.
//   4. Trend chart: recharts LineChart of score % over time, series per mode.
//   5. Recent sessions table.
//   6. Weak areas list.
//
// Empty states:
//   • Loading — calm placeholder, no spinner anxiety.
//   • No sessions yet — calm-mentor coaching tone, reuses the synthesized
//     PMP-practice CTA pattern (close + click cert + click practice).
//
// Voice rules (PLAN-phase-a-design.md §6.1 + §6.5):
//   • No streak/engagement language. No "you're behind."
//   • Phrase regressions as "dipped this week," never "you're slipping."

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import styles from './PerformanceHistory.module.css';
import CoachLayout from '../coach/CoachLayout';
import ScreenHeader from '../shared/ScreenHeader';
import DiscordPromo from '../shared/DiscordPromo';
import { loadPerformanceHistory } from '../../services/performanceHistoryService';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { getAllExams, getExamConfig } from '../../config/exams';

const MODE_LABELS = {
  full:          'Full Exam',
  practice:      'Practice',
  targeted:      'Targeted',
  targetedTest:  'Targeted',
};

// Recharts line color per mode — kept simple, color-blind reasonable.
// Literal hex (not var(--…)) because recharts sets these as SVG presentation
// attributes, where CSS variables don't resolve. Values = theme.css tokens.
const MODE_COLORS = {
  Full:     '#2c63ff', // --brand
  Practice: '#16a571', // --good
  Targeted: '#e9a23b', // --warn
};

// Per-cert "Exam Ready" target, read from src/config/exams.js → pass_target_pct
// (Phase C). When the All-certs filter is active OR the cert lacks the field,
// fall back to the readiness model default of 75 (§4). Phase D will overlay
// a learner-set override on top of this.
const DEFAULT_TARGET_PERCENT = 75;
function resolveTargetPercent(selectedExamId) {
  if (!selectedExamId) return DEFAULT_TARGET_PERCENT;
  try {
    const cfg = getExamConfig(selectedExamId);
    const v = cfg && typeof cfg.pass_target_pct === 'number' ? cfg.pass_target_pct : null;
    if (typeof v === 'number' && v > 0 && v <= 100) return v;
  } catch (_) { /* fall through */ }
  return DEFAULT_TARGET_PERCENT;
}

/**
 * @param {object} props
 * @param {boolean} [props.isOpen=true]  — only used in modal/portal mode
 * @param {function} [props.onClose]
 * @param {function} [props.onStartFirstSession]
 * @param {boolean} [props.embedded=false] — Phase E2: when true, renders as
 *        an inline page (no createPortal, no backdrop) for App.js view='history'
 * @param {function} [props.onNavigate]   — Phase E2: when embedded, supplies a
 *        "Back to dashboard" affordance via onNavigate('dashboard')
 */
export default function PerformanceHistory({ isOpen = true, onClose, onStartFirstSession, embedded = false, onNavigate, onReviewSession, initialExamId = null, onExamChange }) {
  const [state, setState] = useState({ loading: true, sessions: [], summaryByExam: {}, availableExams: [] });

  // Who is looking, so the Review action can be withheld on rows this viewer
  // does not own (see isReviewable). Read from LearnerAuthContext rather than
  // authIdentity's cache: that cache is only populated as a side effect of the
  // first write path, so on a page load that goes straight to History it is
  // still null and the ownership check would silently no-op.
  const { user: viewer } = useLearnerAuth();
  const viewerId = viewer?.id || null;
  // Seeded by the caller so the cert filter survives a trip out to a session
  // review and back, which unmounts this component. null = not yet chosen.
  const [selectedExamId, setSelectedExamId] = useState(initialExamId);

  // When embedded, treat as always open. When portal-mode, gate on isOpen.
  const active = embedded || isOpen;

  // Load once each time the overlay opens (or once for embedded mounts).
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      const result = await loadPerformanceHistory();
      if (cancelled) return;
      setState({ loading: false, ...result });
      // Always focus a single cert (no aggregate "All" view) — default to the
      // first cert with data; the learner can switch via the chips.
      // Keep a selection the learner already made, as long as that cert is
      // still one they have data in; otherwise fall back to the first.
      if (result.availableExams.length >= 1) {
        setSelectedExamId((prev) => (prev && result.availableExams.includes(prev) ? prev : result.availableExams[0]));
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  // Close on Escape (only meaningful in portal mode).
  useEffect(() => {
    if (!active || embedded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, embedded, onClose]);

  const examLabels = useMemo(() => {
    const map = {};
    try { for (const e of getAllExams()) map[e.examId] = e.displayName; } catch (_) {}
    return map;
  }, []);

  if (!active) return null;

  const { loading, sessions, summaryByExam, availableExams } = state;

  // Always scope to a single cert (no aggregate "All" view). Falls back to the
  // first available cert until the load effect sets the selection.
  const effectiveExamId = selectedExamId || availableExams[0] || null;

  const filtered = effectiveExamId
    ? sessions.filter((s) => s.examId === effectiveExamId)
    : sessions;

  const summary = effectiveExamId
    ? summaryByExam[effectiveExamId] || null
    : null;

  // Phase E2: when embedded, render as an inline page. Otherwise (legacy
  // portal mode), preserve the original createPortal/backdrop behavior.
  const inner = (
    <>
      {embedded ? (
        /* Standard topmast (ScreenHeader banner) — coach rides the banner,
           replacing the old inline CoachLayout header. */
        <ScreenHeader
          variant="banner"
          coachPose="notepad"
          onBack={() => onNavigate?.('dashboard')}
          backTestId="performance-history-back"
          eyebrow="Your Certification Journey"
          title={<>Performance<br />History</>}
          titleId="ph-title"
          promo={<DiscordPromo />}
        />
      ) : (
        <>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close performance history"
            onClick={() => onClose?.()}
            data-testid="performance-history-close"
          >×</button>
          <CoachLayout pose="notepad" heightPx={150}>
            <p className={styles.eyebrow}>Your Certification Journey</p>
            <h2 id="ph-title" className={styles.title}>Performance History</h2>
          </CoachLayout>
        </>
      )}

        {loading ? (
          <p className={styles.loading}>Loading your sessions…</p>
        ) : sessions.length === 0 ? (
          <EmptyState onStartFirstSession={onStartFirstSession} onClose={onClose} />
        ) : (
          <>
            {availableExams.length > 1 && (
              <CertFilterChips
                availableExams={availableExams}
                examLabels={examLabels}
                selectedExamId={effectiveExamId}
                onSelect={(examId) => { setSelectedExamId(examId); onExamChange?.(examId); }}
              />
            )}

            <SummaryStrip summary={summary} />

            <TrendChart sessions={filtered} targetPercent={resolveTargetPercent(effectiveExamId)} />

            <WeakAreas weakPhases={summary?.weakPhases || []} />

            <RecentSessionsTable sessions={filtered} examLabels={examLabels} onReviewSession={onReviewSession} viewerId={viewerId} />
          </>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className={styles.page} data-testid="performance-history-page">
        <div className={styles.panel} role="region" aria-labelledby="ph-title">
          {inner}
        </div>
      </div>
    );
  }

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="performance-history-backdrop"
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="ph-title">
        {inner}
      </div>
    </div>,
    document.body,
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function EmptyState({ onStartFirstSession, onClose }) {
  return (
    <div className={styles.empty} data-testid="performance-history-empty">
      <p className={styles.emptyLead}>
        You haven’t taken a tracked session yet. Your first signed-in session sets your baseline.
      </p>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => (onStartFirstSession || onClose)?.()}
        data-testid="performance-history-start-practice"
      >
        Start a PMP practice session  →
      </button>
      <p className={styles.emptyFootnote}>
        You’re not behind. This is just the start of the calibration.
      </p>
    </div>
  );
}

function CertFilterChips({ availableExams, examLabels, selectedExamId, onSelect }) {
  return (
    <div className={styles.chipsRow} role="tablist" aria-label="Filter by certification">
      {availableExams.map((examId) => (
        <button
          key={examId}
          type="button"
          role="tab"
          aria-selected={selectedExamId === examId}
          className={`${styles.chip} ${selectedExamId === examId ? styles.chipActive : ''}`}
          onClick={() => onSelect(examId)}
          data-testid={`cert-filter-${examId}`}
        >
          {examLabels[examId] || examId}
        </button>
      ))}
    </div>
  );
}

function SummaryStrip({ summary }) {
  if (!summary) return null;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (_) { return '—'; }
  };
  return (
    <div className={styles.summaryStrip} data-testid="performance-history-summary">
      <SummaryCard label="Sessions"     value={summary.count} />
      <SummaryCard label="Best score"   value={summary.bestPct != null ? `${summary.bestPct}%` : '—'} />
      <SummaryCard label="Average"      value={summary.avgPct  != null ? `${summary.avgPct}%`  : '—'} />
      <SummaryCard label="Last session" value={fmtDate(summary.lastAttemptAt)} />
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryValue}>{value}</div>
      <div className={styles.summaryLabel}>{label}</div>
    </div>
  );
}

function TrendChart({ sessions, targetPercent = DEFAULT_TARGET_PERCENT }) {
  // Build chart data, oldest → newest, with one row per session and a numeric
  // value per mode series. Recharts handles missing values in series.
  const rows = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];
    const sorted = [...sessions].sort(
      (a, b) => new Date(a.startedAt) - new Date(b.startedAt),
    );
    return sorted.map((s) => {
      const seriesKey = MODE_LABELS[s.mode] || 'Practice';
      const row = {
        date: new Date(s.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      };
      row[seriesKey] = s.scorePercentage;
      return row;
    });
  }, [sessions]);

  if (rows.length === 0) return null;

  // Pick which series to render (only ones that actually have data).
  const seriesPresent = ['Full', 'Practice', 'Targeted'].filter((s) =>
    rows.some((r) => typeof r[s] === 'number'),
  );

  return (
    <div className={styles.chartWrap} data-testid="performance-history-chart">
      <h3 className={styles.sectionHeading}>Score over time</h3>
      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 8, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e8f1" />
            <XAxis dataKey="date" stroke="#5a6582" fontSize={12} />
            <YAxis domain={[0, 100]} stroke="#5a6582" fontSize={12} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v) => (v == null ? '—' : `${v}%`)} />
            <Legend />
            {/* Exam-ready target reference line. Reads per-cert pass_target_pct
                from src/config/exams.js (Phase C); Phase D will overlay a
                learner-set personal target on top of this. */}
            <ReferenceLine
              y={targetPercent}
              stroke="#cfd5e6"
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={{
                value: `Target — ${targetPercent}%`,
                position: 'insideTopRight',
                fill: '#5a6582',
                fontSize: 11,
              }}
            />
            {seriesPresent.map((s) => (
              <Line
                key={s}
                type="monotone"
                dataKey={s}
                stroke={MODE_COLORS[s] || '#5a6582'}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className={styles.chartCaption}>
        Dashed line at {targetPercent}% is the Exam Ready threshold — consistently scoring at or
        above the line on practice sessions is a strong readiness signal.
      </p>
    </div>
  );
}

function WeakAreas({ weakPhases }) {
  if (!weakPhases || weakPhases.length === 0) return null;
  return (
    <div className={styles.weakAreas} data-testid="performance-history-weak-areas">
      <h3 className={styles.sectionHeading}>Areas to strengthen</h3>
      <ul className={styles.weakList}>
        {weakPhases.map((w) => (
          <li key={w.phase} className={styles.weakItem}>
            <span className={styles.weakPhase}>{w.phase}</span>
            <span className={styles.weakAccuracy}>{w.accuracy}%</span>
            <span className={styles.weakHint}>
              ({w.correct} correct of {w.total})
            </span>
          </li>
        ))}
      </ul>
      <p className={styles.weakFootnote}>
        A focused 15-question session on the weakest of these often shifts the curve.
      </p>
    </div>
  );
}

/**
 * A completed attempt is reviewable when it recorded at least one answer.
 * Unfinished-looking rows (nothing answered) get no action rather than a
 * misleading one — and nothing here ever resumes a session; reviewing a past
 * attempt is read-only.
 */
export function isReviewable(session, viewerId) {
  if (!session || !session.sessionId) return false;
  const answered = (session.scoreCorrect || 0) + (session.scoreIncorrect || 0);
  if (answered <= 0) return false;
  // Reviewing is owner-only: get_session_review matches the row against
  // auth.uid() and returns NULL for anyone else. The exam_sessions_select
  // policy is broader — admins read every learner's rows — so an admin's
  // History lists sessions this viewer cannot open. Offering a Review button
  // there produces a dead end, so hide it when the row is positively known to
  // belong to someone else. Unknown ownership on either side leaves the button
  // alone rather than hiding one that works.
  if (viewerId && session.userId && session.userId !== viewerId) return false;
  return true;
}

const REASON_SHORT = { early: 'Ended early', expired: 'Time expired' };

function RecentSessionsTable({ sessions, examLabels, onReviewSession, viewerId }) {
  if (!sessions || sessions.length === 0) return null;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch (_) { return '—'; }
  };
  return (
    <div className={styles.tableWrap} data-testid="performance-history-table">
      <h3 className={styles.sectionHeading}>Recent sessions</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Cert</th>
            <th>Mode</th>
            <th className={styles.numCell}>Questions</th>
            <th className={styles.numCell}>Score</th>
            <th className={styles.actionCell}><span className={styles.srOnly}>Review</span></th>
          </tr>
        </thead>
        <tbody>
          {sessions.slice(0, 20).map((s) => (
            <tr key={s.sessionId}>
              <td>{fmtDate(s.startedAt)}</td>
              <td>{examLabels[s.examId] || s.examId}</td>
              <td>{MODE_LABELS[s.mode] || s.mode}</td>
              <td className={styles.numCell}>{s.totalQuestions || '—'}</td>
              <td className={styles.numCell}>
                {typeof s.scorePercentage === 'number' ? `${s.scorePercentage}%` : '—'}
                {REASON_SHORT[s.completionReason] && (
                  <span className={styles.reasonNote}>{REASON_SHORT[s.completionReason]}</span>
                )}
              </td>
              <td className={styles.actionCell}>
                {isReviewable(s, viewerId) && onReviewSession && (
                  <button
                    type="button"
                    className={styles.reviewButton}
                    data-testid={`performance-history-review-${s.sessionId}`}
                    onClick={() => onReviewSession(s.sessionId)}
                    aria-label={`Review your ${examLabels[s.examId] || s.examId} session from ${fmtDate(s.startedAt)}`}
                  >
                    Review
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sessions.length > 20 && (
        <p className={styles.tableFootnote}>Showing 20 most recent of {sessions.length}.</p>
      )}
    </div>
  );
}

